import React, { useState, useEffect } from "react";
import Header from "../../Components/Header";
import Seo from "../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { StatementReportService } from "../../Services/ApiTestService/apiTestService";
import { Button } from "../../Components/Tags/Tags";
import Loader from "../../Loader";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { FaEye, FaTimes } from "react-icons/fa";
import { FiFileText, FiDownload } from "react-icons/fi";
import html2pdf from "html2pdf.js";

const StatementReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });
  const [perPage, setPerPage] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [pagination.current_page, perPage]);

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const payload = {
        from_date: fromDate,
        to_date: toDate,
        total_data: perPage.toString(),
        page: pagination?.current_page,
      };

      const response = await StatementReportService(payload);

      if (response?.status?.code || response?.status === 200) {
        setReportData(response.data || []);
        setPagination(response.pagination || {});
      } else {
        showErrorToast("Failed to fetch statement reports");
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      showErrorToast(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current_page: 1 });
    fetchReports();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination({ ...pagination, current_page: page });
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(parseInt(e.target.value));
    setPagination({ ...pagination, current_page: 1 });
  };

  const handlePrevious = () => {
    if (pagination.current_page > 1) {
      handlePageChange(pagination.current_page - 1);
    }
  };

  const handleNext = () => {
    if (pagination.current_page < pagination.last_page) {
      handlePageChange(pagination.current_page + 1);
    }
  };

  // Modal handlers
  const handleViewReport = (report) => {
    setSelectedReport(report?.report);
    setShowModal(true);
    console.log(report);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  // Download JSON Report
  const downloadJSONReport = () => {
    if (!selectedReport) return;

    const dataStr = JSON.stringify(selectedReport, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `statement-report-${selectedReport.txn_id || "report"}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccessToast("JSON report downloaded successfully");
  };

  // Download PDF Report
// const downloadPDFReport = async () => {
//   console.log("download pdf clicked");

//   const element = document.getElementById("bank-statement-report");

//   if (!element) {
//     showErrorToast("Report content not found");
//     return;
//   }

//   setIsLoading(true);

//   const scrollContainer = element.closest(".overflow-y-auto");

//   // Save original styles
//   const originalElementHeight = element.style.height;
//   const originalElementOverflow = element.style.overflow;

//   const originalContainerHeight = scrollContainer?.style.height || "";
//   const originalContainerOverflow = scrollContainer?.style.overflow || "";

//   try {
//     if (scrollContainer) {
//       scrollContainer.style.height = "auto";
//       scrollContainer.style.overflow = "visible";
//     }

//     element.style.height = "auto";
//     element.style.overflow = "visible";

//     await new Promise((resolve) => setTimeout(resolve, 500));

//     const opt = {
//       margin: 10,

//       filename: `bank-statement-${selectedReport?.txn_id || "report"}.pdf`,

//       image: {
//         type: "jpeg",
//         quality: 0.98,
//       },

//       html2canvas: {
//         scale: 1.5,
//         useCORS: true,
//         allowTaint: false,
//         backgroundColor: "#ffffff",

//         scrollX: 0,
//         scrollY: 0,

//         windowWidth: element.scrollWidth,
//         windowHeight: element.scrollHeight,

//         logging: true,
//       },

//       jsPDF: {
//         unit: "mm",
//         format: "a4",
//         orientation: "portrait",
//         compress: true,
//       },

//       pagebreak: {
//         mode: ["css", "legacy"],
//       },
//     };

//     await html2pdf()
//       .set(opt)
//       .from(element)
//       .toPdf()
//       .save();

//     showSuccessToast("PDF downloaded successfully");
//   } catch (error) {
//     console.error("PDF generation error:", error);
//     showErrorToast("Failed to generate PDF");
//   } finally {
//     // Restore original styles
//     element.style.height = originalElementHeight;
//     element.style.overflow = originalElementOverflow;

//     if (scrollContainer) {
//       scrollContainer.style.height = originalContainerHeight;
//       scrollContainer.style.overflow = originalContainerOverflow;
//     }

//     setIsLoading(false);
//   }
// };

const downloadPDFReport = () => {
  console.log("print pdf clicked");

  const element = document.getElementById("bank-statement-report");

  if (!element) {
    showErrorToast("Report content not found");
    return;
  }

  setIsLoading(true);

  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    setIsLoading(false);
    showErrorToast("Please allow popups to print the report");
    return;
  }

  // Get all styles from the current page
  const styles = Array.from(
    document.querySelectorAll("style, link[rel='stylesheet']")
  )
    .map((style) => style.outerHTML)
    .join("\n");

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Bank Statement - ${selectedReport?.txn_id || "Report"}</title>

        ${styles}

        <style>
          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }

          body {
            color: #000000 !important;
            font-size: 11px;
          }

          #bank-statement-report {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: #ffffff !important;
            color: #000000 !important;
          }

          #bank-statement-report table {
            width: 100% !important;
            border-collapse: collapse;
          }

          #bank-statement-report tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          #bank-statement-report thead {
            display: table-header-group;
          }

          #bank-statement-report tfoot {
            display: table-footer-group;
          }

          .page-break-before {
            page-break-before: always;
            break-before: page;
          }

          .page-break-after {
            page-break-after: always;
            break-after: page;
          }

          .page-break-avoid {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            #downloadBtn{
            display: none;
            }
          }
        </style>
      </head>

      <body>
        ${element.outerHTML}
      </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for the new document to finish rendering
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();

      setTimeout(() => {
        printWindow.close();
        setIsLoading(false);
      }, 500);
    }, 500);
  };
};

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const badgeClass =
      status === "success"
        ? "bg-green-500 text-white"
        : "bg-yellow-500 text-white";
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badgeClass}`}
      >
        {status?.toUpperCase()}
      </span>
    );
  };

  const renderPageNumbers = () => {
    const pages = [];

    for (let i = 1; i <= pagination.last_page; i++) {
      if (
        i === 1 ||
        i === pagination.last_page ||
        (i >= pagination.current_page - 1 && i <= pagination.current_page + 1)
      ) {
        pages.push(
          <li key={i}>
            <button
              onClick={() => handlePageChange(i)}
              className={`flex items-center justify-center px-3 h-9 leading-tight border ${
                pagination.current_page === i
                  ? "text-blue-600 bg-blue-50 border-blue-300 hover:bg-blue-100 hover:text-blue-700"
                  : "text-gray-500 bg-white border-gray-300 hover:bg-gray-100 hover:text-gray-700"
              } transition-colors duration-200`}
            >
              {i}
            </button>
          </li>,
        );
      } else if (
        i === pagination.current_page - 2 ||
        i === pagination.current_page + 2
      ) {
        pages.push(
          <li key={i}>
            <span className="flex items-center justify-center px-3 h-9 leading-tight text-gray-500 bg-white border border-gray-300">
              ...
            </span>
          </li>,
        );
      }
    }
    return pages;
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading="Statement Analyzer Report" />

      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Records Per Page
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white cursor-pointer"
                  value={perPage}
                  onChange={handlePerPageChange}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  disabled={isLoading}
                  className="w-full px-2 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? "Searching..." : "Search Reports"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50">
                <tr className="bg-blue-700">
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    SN
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Client Ref
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Transaction ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Request ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Stage
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Created At
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-bold bg-blue-700 text-white uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.length > 0 ? (
                  reportData.map((report, index) => (
                    <tr
                      key={report.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {(pagination.current_page - 1) * perPage + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs text-gray-600">
                          {report.client_ref_num}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {report.txn_id}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                          {report.request_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(report.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-gray-200 text-gray-800 text-xs font-semibold rounded-full">
                          {report.stage}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(report.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewReport(report)}
                          className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors duration-200"
                          title="View Details"
                        >
                          <FaEye className="mr-1" /> View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg
                          className="w-16 h-16 mb-4 text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                          />
                        </svg>
                        <p className="text-lg font-medium">No records found</p>
                        <p className="text-sm text-gray-400 mt-1">
                          Try adjusting your search filters
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Section */}
          {reportData.length > 0 && (
            <div className="bg-white px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700 mb-4 sm:mb-0">
                Showing{" "}
                <span className="font-semibold">
                  {(pagination.current_page - 1) * perPage + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold">
                  {Math.min(
                    pagination.current_page * perPage,
                    pagination.total,
                  )}
                </span>{" "}
                of <span className="font-semibold">{pagination.total}</span>{" "}
                entries
              </div>

              <nav aria-label="Pagination">
                <ul className="inline-flex -space-x-px text-sm">
                  <li>
                    <button
                      onClick={handlePrevious}
                      disabled={pagination.current_page === 1}
                      className={`flex items-center justify-center px-3 h-9 ml-0 leading-tight rounded-l-lg border border-gray-300 transition-colors duration-200 ${
                        pagination.current_page === 1
                          ? "text-gray-400 bg-gray-100 cursor-not-allowed opacity-50"
                          : "text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700"
                      }`}
                    >
                      Previous
                    </button>
                  </li>

                  {renderPageNumbers()}

                  <li>
                    <button
                      onClick={handleNext}
                      disabled={
                        pagination.current_page === pagination.last_page
                      }
                      className={`flex items-center justify-center px-3 h-9 leading-tight rounded-r-lg border border-gray-300 transition-colors duration-200 ${
                        pagination.current_page === pagination.last_page
                          ? "text-gray-400 bg-gray-100 cursor-not-allowed opacity-50"
                          : "text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700"
                      }`}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* MODAL - View Report Details */}
      {showModal && selectedReport && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-1">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[100vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-2 py-2 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FiFileText /> Statement Report Details
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-white hover:bg-blue-800 rounded-full p-2 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-1">
              <div className="w-full">
                {/* PDF Preview - Right Side */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div
                    id="bank-statement-report"
                    className="bg-white p-1 text-xs overflow-visible h-auto"
                  >
                    {/* Header Section */}
                    <div className="border border-gray-300 p-4 mb-4 page-break-avoid">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-700 p-2 rounded">
                            <FiFileText className="text-white text-lg" />
                          </div>
                          <div>
                            <h2 className="text-lg font-bold text-gray-900">
                              Bank Statement Analysis Report
                            </h2>
                            <p className="text-xs text-gray-600 mt-1">
                              Transaction ID: {selectedReport?.txn_id}
                            </p>
                          </div>
                        </div>
                        <Button id="downloadBtn"
                          type="button"
                          text={
                            <span className="flex items-center gap-2">
                              <FiDownload size={14} />
                              Download PDF
                            </span>
                          }
                          onClick={downloadPDFReport}
                          className="bg-green-700 text-white px-4 py-2 rounded text-xs font-semibold hover:bg-green-800 transition-all"
                        />
                      </div>

                      {/* Statement Period Info */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="bg-blue-50 p-3 rounded border border-blue-200">
                          <p className="text-xs text-blue-700 font-semibold uppercase mb-1">
                            Statement Period
                          </p>
                          <p className="text-xs font-bold text-blue-900 leading-tight">
                            {selectedReport?.statement_start_date} to{" "}
                            {selectedReport?.statement_end_date}
                          </p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded border border-purple-200">
                          <p className="text-xs text-purple-700 font-semibold uppercase mb-1">
                            Duration
                          </p>
                          <p className="text-xs font-bold text-purple-900">
                            {selectedReport?.duration_in_month} Months
                          </p>
                        </div>
                        <div className="bg-amber-50 p-3 rounded border border-amber-200">
                          <p className="text-xs text-amber-700 font-semibold uppercase mb-1">
                            Request ID
                          </p>
                          <p className="text-xs font-bold text-amber-900">
                            {selectedReport?.request_id}
                          </p>
                        </div>
                        <div className="bg-green-50 p-3 rounded border border-green-200">
                          <p className="text-xs text-green-700 font-semibold uppercase mb-1">
                            Status
                          </p>
                          <p className="text-xs font-bold text-green-900 uppercase">
                            {selectedReport?.status?.type ||
                              selectedReport?.status}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Customer Information */}
                    <div className="border border-gray-300 p-4 mb-4 page-break-avoid">
                      <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1 h-5 bg-green-700 rounded-full"></span>
                        Customer Information
                      </h3>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                            Account Holder Name
                          </span>
                          <span className="text-xs font-bold text-gray-900">
                            {selectedReport?.customer_info?.name || "N/A"}
                          </span>
                        </div>

                        <div>
                          <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                            Email
                          </span>
                          <span className="text-xs text-gray-900">
                            {selectedReport?.customer_info?.email || "N/A"}
                          </span>
                        </div>

                        <div>
                          <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                            Contact Number
                          </span>
                          <span className="text-xs text-gray-900">
                            {selectedReport?.customer_info?.contact_number ||
                              "N/A"}
                          </span>
                        </div>

                        <div>
                          <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                            PAN
                          </span>
                          <span className="text-xs text-gray-900">
                            {selectedReport?.customer_info?.pan || "N/A"}
                          </span>
                        </div>

                        <div>
                          <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                            Address
                          </span>
                          <span className="text-xs text-gray-900 leading-tight">
                            {selectedReport?.customer_info?.address || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Account Details */}
                    {selectedReport?.accounts?.map((account, idx) => (
                      <div
                        key={idx}
                        className="border border-gray-300 p-4 mb-4 page-break-before"
                      >
                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <span className="w-1 h-5 bg-blue-700 rounded-full"></span>
                          Account Details
                        </h3>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Institution ID
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.institution_id || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Opening Date
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.account_opening_date || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Bank Name
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.bank}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Number
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.account_number}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Type
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.account_type}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Description
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.account_description}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Branch
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.location}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              IFSC Code
                            </span>
                            <span className="text-xs font-mono text-gray-900">
                              {account.ifsc_code}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              MICR Code
                            </span>
                            <span className="text-xs font-mono text-gray-900">
                              {account.micr_code}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              OD Limit
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.od_limit || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Drawing Power
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.drawing_power || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Transaction Start Date
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.transaction_start_date || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Transaction End Date
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.transaction_end_date || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Transactions Table */}
                        <div className="mt-4">
                          <h4 className="text-xs font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <span className="w-0.5 h-4 bg-indigo-700 rounded-full"></span>
                            Transaction History (
                            {account.transactions?.length || 0})
                          </h4>

                          <div className="overflow-x-auto border border-gray-300 rounded">
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="bg-gray-800 text-white">
                                  <th className="px-2 py-2 text-left font-semibold border-r border-gray-600">
                                    Date
                                  </th>
                                  <th className="px-2 py-2 text-left font-semibold border-r border-gray-600">
                                    Description
                                  </th>
                                  <th className="px-2 py-2 text-right font-semibold border-r border-gray-600">
                                    Debit (₹)
                                  </th>
                                  <th className="px-2 py-2 text-right font-semibold border-r border-gray-600">
                                    Credit (₹)
                                  </th>
                                  <th className="px-2 py-2 text-right font-semibold border-r border-gray-600">
                                    Balance (₹)
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {account.transactions?.map((txn, txnIdx) => (
                                  <tr
                                    key={txnIdx}
                                    className={`${
                                      txnIdx % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-50"
                                    } border-b border-gray-200 page-break-avoid`}
                                  >
                                    <td className="px-2 py-2 whitespace-nowrap font-medium text-gray-900 border-r border-gray-200">
                                      {txn.date}
                                    </td>
                                    <td className="px-2 py-2 text-gray-700 max-w-xs border-r border-gray-200">
                                      <div
                                        className="truncate text-xs leading-tight"
                                        title={txn.narration}
                                      >
                                        {txn.narration}
                                      </div>
                                    </td>
                                    <td className="px-2 py-2 text-right font-semibold border-r border-gray-200">
                                      {parseFloat(txn.amount) < 0 ? (
                                        <span className="text-red-700">
                                          {Math.abs(
                                            parseFloat(txn.amount),
                                          ).toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-2 text-right font-semibold border-r border-gray-200">
                                      {parseFloat(txn.amount) > 0 ? (
                                        <span className="text-green-700">
                                          {parseFloat(txn.amount).toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-2 text-right font-bold text-gray-900 border-r border-gray-200">
                                      {parseFloat(txn.balance).toFixed(2)}
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                          txn.tamper_flag === "GREEN"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {txn.tamper_flag === "GREEN"
                                          ? "✓"
                                          : "✗"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Tamper Detection Section */}
                    {selectedReport?.tamper_detection_details && (
                      <div className="border border-gray-300 p-4 mb-4 page-break-avoid">
                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <span className="w-1 h-5 bg-red-700 rounded-full"></span>
                          Security & Fraud Analysis
                        </h3>

                        {selectedReport.tamper_detection_details.map(
                          (detail, idx) => (
                            <div
                              key={idx}
                              className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                            >
                              <div className="bg-green-50 p-3 rounded border border-green-200">
                                <span className="text-xs text-green-700 uppercase font-semibold block mb-1">
                                  PDF Confidence Score
                                </span>
                                <span className="text-lg font-bold text-green-900">
                                  {detail.original_pdf_confidence_score}%
                                </span>
                              </div>
                              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                <span className="text-xs text-blue-700 uppercase font-semibold block mb-1">
                                  Fraud Analysis Score
                                </span>
                                <span className="text-lg font-bold text-blue-900">
                                  {detail.additional_fraud_analysis_score}%
                                </span>
                              </div>
                              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                                <span className="text-xs text-purple-700 uppercase font-semibold block mb-1">
                                  Tampering Detected
                                </span>
                                <span className="text-lg font-bold text-purple-900">
                                  {detail.txn_amt_bal_tampering_detected}
                                </span>
                              </div>

                              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                                <span className="text-xs text-yellow-700 uppercase font-semibold block mb-1">
                                  Standard Bank Font
                                </span>
                                <span className="text-lg font-bold text-yellow-900">
                                  {detail.is_standard_bank_font === 1
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </div>

                              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                <span className="text-xs text-orange-700 uppercase font-semibold block mb-1">
                                  Standard PDF Font
                                </span>
                                <span className="text-lg font-bold text-orange-900">
                                  {detail.is_standard_pdf_font === 1
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </div>

                              <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
                                <span className="text-xs text-indigo-700 uppercase font-semibold block mb-1">
                                  Tampered Transactions
                                </span>
                                <span className="text-lg font-bold text-indigo-900">
                                  {detail.specific_amt_bal_tampered_txn_detected ||
                                    "N/A"}
                                </span>
                              </div>

                              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                <span className="text-xs text-gray-700 uppercase font-semibold block mb-1">
                                  Overall Balance Difference
                                </span>
                                <span className="text-lg font-bold text-gray-900">
                                  {detail.overall_balance_difference || "0.0"}
                                </span>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-100 px-6 py-4 flex items-center justify-end gap-3 border-t">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StatementReport;
